import { useEffect, useRef, useState } from "react";
import portalApi, { portalToken, errMsg } from "@/lib/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, KeyRound } from "lucide-react";

export const PortalLogin = ({ linkToken, onIn }) => {
  const [step, setStep] = useState(linkToken ? "starting" : "email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (!linkToken || started.current) { return; }
    started.current = true;
    portalApi.post("/link-start", { token: linkToken })
      .then((r) => { setHint(r.data.email_hint); setStep("code"); })
      .catch((e) => { setErr(errMsg(e, "This link has expired")); setStep("email"); });
  }, [linkToken]);

  const sendCode = async () => {
    setBusy(true); setErr("");
    try { await portalApi.post("/request-code", { email }); setHint(email); setStep("code"); }
    catch (e) { setErr(errMsg(e, "Could not send code — try again")); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setErr("");
    try {
      const body = linkToken && !email ? { token: linkToken, code } : { email, code };
      const { data } = await portalApi.post("/verify", body);
      portalToken.set(data.token);
      onIn();
    } catch (e) { setErr(errMsg(e, "Could not verify — try again")); }
    finally { setBusy(false); }
  };

  if (step === "starting") return <Loader2 className="mx-auto h-6 w-6 animate-spin text-teal-400" />;
  return (
    <div className="mx-auto w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" data-testid="portal-login">
      {step === "email" ? (
        <>
          <div className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Mail className="h-5 w-5 text-teal-600" /> Sign in</div>
          <p className="mt-1 text-sm text-slate-500">Enter the email address the clinic has for you. We'll send a 6-digit code.</p>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-4" data-testid="portal-email-input" />
          <Button onClick={sendCode} disabled={busy || !email.includes("@")} className="mt-3 w-full bg-teal-600 hover:bg-teal-700" data-testid="portal-send-code-btn">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Send my code
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><KeyRound className="h-5 w-5 text-teal-600" /> Enter your code</div>
          <p className="mt-1 text-sm text-slate-500" data-testid="portal-code-hint">If {hint} is registered with the clinic, a 6-digit code has been sent there. It expires in 10 minutes.</p>
          <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="mt-4 text-center font-mono text-2xl tracking-[0.4em]" data-testid="portal-code-input" />
          <Button onClick={verify} disabled={busy || code.length !== 6} className="mt-3 w-full bg-teal-600 hover:bg-teal-700" data-testid="portal-verify-btn">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Continue
          </Button>
          <button onClick={() => { setStep("email"); setCode(""); }} className="mt-3 w-full text-xs text-slate-500 hover:underline" data-testid="portal-use-email-btn">Use a different email / send a new code</button>
        </>
      )}
      {err && <p className="mt-3 text-sm text-red-600" data-testid="portal-login-error">{err}</p>}
    </div>
  );
};
